import React, { useEffect, useState } from 'react';
import { ShieldAlert } from 'lucide-react';
import { isTouchDevice, obscureContent, playbackState, reportSecurityEvent } from './securityReporter';

// Best-effort content protection for a page: no copy / right-click / save / print, and admin alerts for
// screenshot keys, devtools and printing. Browsers cannot block real screen recording.
const ProtectedContent = ({ children, reportTabHidden = false }) => {
  const [warning, setWarning] = useState(null);

  useEffect(() => {
    document.body.classList.add('secure-mode');
    const warn = (text) => {
      setWarning(text);
      setTimeout(() => setWarning(null), 4000);
    };

    const isEditable = (el) => el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);

    const onContextMenu = (e) => {
      e.preventDefault();
      reportSecurityEvent('context_menu');
    };
    const onCopy = (e) => {
      if (isEditable(e.target)) return;
      e.preventDefault();
      reportSecurityEvent('copy_attempt');
    };
    const onDragStart = (e) => e.preventDefault();

    const onKeyDown = (e) => {
      const key = (e.key || '').toLowerCase();
      const mod = e.ctrlKey || e.metaKey;
      if (key === 'printscreen') {
        obscureContent(4000, 'screenshot');
        reportSecurityEvent('screenshot_key');
        warn('Screenshots are not allowed. This has been reported to the administrator.');
        return;
      }
      if (e.metaKey && e.shiftKey && ['3', '4', '5'].includes(key)) {
        obscureContent(4000, 'screenshot');
        reportSecurityEvent('screen_capture_shortcut', { keys: `Cmd+Shift+${key}` });
        warn('Screen capture is not allowed. This has been reported to the administrator.');
        return;
      }
      if (key === 's' && e.shiftKey && (e.metaKey || (e.getModifierState && e.getModifierState('OS')))) {
        obscureContent(4000, 'screenshot');
        reportSecurityEvent('screen_capture_shortcut', { keys: 'Win+Shift+S' });
        warn('Screen capture is not allowed. This has been reported to the administrator.');
        return;
      }
      if (key === 'f12' || (mod && e.shiftKey && ['i', 'j', 'c'].includes(key)) || (mod && key === 'u')) {
        e.preventDefault();
        reportSecurityEvent('devtools_shortcut', { key: e.key });
        return;
      }
      if (mod && key === 's') {
        e.preventDefault();
        reportSecurityEvent('download_attempt');
        return;
      }
      if (mod && key === 'p') {
        e.preventDefault();
        reportSecurityEvent('print_attempt');
        warn('Printing is disabled on this page.');
      }
    };
    // PrintScreen often only fires on keyup (Windows)
    const onKeyUp = (e) => {
      if ((e.key || '').toLowerCase() === 'printscreen') {
        obscureContent(4000, 'screenshot');
        reportSecurityEvent('screenshot_key');
        try {
          navigator.clipboard && navigator.clipboard.writeText('Screenshots of Altanon Learn content are not allowed.');
        } catch (err) {
          /* clipboard may be unavailable */
        }
        warn('Screenshots are not allowed. This has been reported to the administrator.');
      }
    };
    const onBeforePrint = () => reportSecurityEvent('print_attempt');
    const onVisibility = () => {
      if (document.hidden && reportTabHidden && !playbackState.playing) reportSecurityEvent('tab_hidden');
    };

    // Docked devtools suddenly make the page much smaller than the browser window. Compare against the
    // gap measured when the page opened, so browser zoom, sidebars or embedded browsers do not count.
    let devtoolsOpen = false;
    let suspect = 0;
    const baseW = window.outerWidth - window.innerWidth;
    const baseH = window.outerHeight - window.innerHeight;
    const devtoolsTimer = isTouchDevice()
      ? null
      : setInterval(() => {
          const gapGrew = window.outerWidth - window.innerWidth - baseW > 200 || window.outerHeight - window.innerHeight - baseH > 200;
          // must hold for two checks in a row so a passing resize is not reported
          suspect = gapGrew ? suspect + 1 : 0;
          const open = suspect >= 2;
          if (open && !devtoolsOpen) {
            reportSecurityEvent('devtools_open');
            warn('Developer tools are not allowed while viewing course content.');
          }
          if (open) obscureContent(2000, 'devtools');
          devtoolsOpen = open;
        }, 1500);

    document.addEventListener('contextmenu', onContextMenu);
    document.addEventListener('copy', onCopy);
    document.addEventListener('cut', onCopy);
    document.addEventListener('dragstart', onDragStart);
    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('keyup', onKeyUp, true);
    window.addEventListener('beforeprint', onBeforePrint);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.body.classList.remove('secure-mode');
      document.removeEventListener('contextmenu', onContextMenu);
      document.removeEventListener('copy', onCopy);
      document.removeEventListener('cut', onCopy);
      document.removeEventListener('dragstart', onDragStart);
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('keyup', onKeyUp, true);
      window.removeEventListener('beforeprint', onBeforePrint);
      document.removeEventListener('visibilitychange', onVisibility);
      if (devtoolsTimer) clearInterval(devtoolsTimer);
    };
  }, [reportTabHidden]);

  return (
    <div className="secure-content relative">
      {warning && (
        <div
          className="fixed top-24 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 bg-red-600 text-white px-4 py-3 rounded-xl shadow-lg font-figtree text-sm"
          role="alert"
          data-testid="security-warning"
        >
          <ShieldAlert className="w-5 h-5" /> {warning}
        </div>
      )}
      {children}
    </div>
  );
};

export default ProtectedContent;
